import { Box, Group } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { ReactNode, useEffect, useRef, useState } from "react";

import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import {
  getInputBackgroundColor,
  getInputBorderColor,
  getInputErrorBackgroundColor,
  getInputErrorShadow,
  getInputTextColor,
  inputErrorBorderColor,
  inputFocusBorderColor,
  inputRadius,
} from "#app/theme/v2/inputStates";
import { spacing } from "#app/theme/v2/spacing";

import { Text, TEXT_LETTER_SPACING, TEXT_LINE_HEIGHTS } from "../Typographies/Text";
import classes from "./InputDuration.module.css";

type DurationUnit = "hours" | "minutes" | "seconds";

const UNIT_LABELS: Record<DurationUnit, string> = {
  hours: "h",
  minutes: "min",
  seconds: "s",
};

const UNIT_MAX: Partial<Record<DurationUnit, number>> = {
  minutes: 59,
  seconds: 59,
};

const UNITS = Object.keys(UNIT_LABELS) as DurationUnit[];

const formatUnit = (value: number) => String(value).padStart(2, "0");

type DurationValue = {
  hours: number;
  minutes: number;
  seconds: number;
};

type InputDurationProps = {
  hours?: number;
  minutes?: number;
  seconds?: number;
  onHoursChange?: (value: number) => void;
  onMinutesChange?: (value: number) => void;
  onSecondsChange?: (value: number) => void;
  onCommit?: (duration: DurationValue) => void | false;
  disabled?: boolean;
  error?: boolean;
  showIcon?: boolean;
  icon?: ReactNode;
  onIconClick?: () => void;
  iconLabel?: string;
};

const InputDuration = ({
  hours = 0,
  minutes = 0,
  seconds = 0,
  onHoursChange,
  onMinutesChange,
  onSecondsChange,
  onCommit,
  disabled,
  error,
  showIcon = true,
  icon,
  onIconClick,
  iconLabel,
}: InputDurationProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const committedValues: Record<DurationUnit, number> = {
    hours,
    minutes,
    seconds,
  };
  const [inputValues, setInputValues] = useState<Record<DurationUnit, string>>({
    hours: formatUnit(hours),
    minutes: formatUnit(minutes),
    seconds: formatUnit(seconds),
  });

  useEffect(() => {
    setInputValues({
      hours: formatUnit(hours),
      minutes: formatUnit(minutes),
      seconds: formatUnit(seconds),
    });
  }, [hours, minutes, seconds]);

  const onChangeHandlers: Record<DurationUnit, ((value: number) => void) | undefined> = {
    hours: onHoursChange,
    minutes: onMinutesChange,
    seconds: onSecondsChange,
  };

  const handleGroupBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (containerRef.current?.contains(event.relatedTarget as Node | null)) {
      return;
    }

    const nextValues = { ...inputValues };
    const nextDuration = { ...committedValues };
    const changedUnits: DurationUnit[] = [];

    UNITS.forEach((unit) => {
      const raw = inputValues[unit];
      const parsed = Number(raw);

      if (raw === "" || Number.isNaN(parsed)) {
        nextValues[unit] = formatUnit(committedValues[unit]);
        return;
      }

      const max = UNIT_MAX[unit];
      const nonNegative = Math.max(0, Math.trunc(parsed));
      const clamped = max === undefined ? nonNegative : Math.min(nonNegative, max);

      nextValues[unit] = formatUnit(clamped);
      nextDuration[unit] = clamped;

      if (clamped !== committedValues[unit]) {
        changedUnits.push(unit);
      }
    });

    if (changedUnits.length > 0 && onCommit?.(nextDuration) === false) {
      setInputValues({
        hours: formatUnit(committedValues.hours),
        minutes: formatUnit(committedValues.minutes),
        seconds: formatUnit(committedValues.seconds),
      });
      return;
    }

    changedUnits.forEach((unit) => {
      onChangeHandlers[unit]?.(nextDuration[unit]);
    });

    setInputValues(nextValues);
  };

  const hasValue = hours > 0 || minutes > 0 || seconds > 0;
  const trailingIcon = icon ?? (
    <IconPencil size={16} color={disabled ? colors.icon.primaryDisabled : colors.icon.secondary} />
  );

  return (
    <Box
      ref={containerRef}
      onBlur={handleGroupBlur}
      p={spacing[2]}
      className={classes.container}
      data-error={error || undefined}
      data-testid="input-duration"
      style={
        {
          display: "flex",
          alignItems: "center",
          borderRadius: inputRadius,
          "--container-bg": getInputBackgroundColor({ disabled, hasValue }),
          "--container-bd": getInputBorderColor({ disabled, hasValue }),
          "--container-focus-bd": inputFocusBorderColor,
          "--container-error-bd": inputErrorBorderColor,
          "--container-error-bg": getInputErrorBackgroundColor({ hasValue }),
          "--container-error-shadow": getInputErrorShadow({ hasValue }),
          "--field-fz": fontSizes[14],
          "--field-fw": fontWeights.regular,
          "--field-lh": TEXT_LINE_HEIGHTS.small,
          "--field-ls": TEXT_LETTER_SPACING.regular.small,
          "--field-color": getInputTextColor({ disabled, hasValue }),
          "--field-disabled-placeholder-color": colors.text.secondaryDisabled,
        } as React.CSSProperties
      }
    >
      <Group gap={spacing[2]} wrap="nowrap" align="center" px={spacing[1]} style={{ flex: 1 }}>
        {UNITS.map((unit) => (
          <Group key={unit} gap={spacing[0.5]} wrap="nowrap" align="center">
            <input
              className={classes.field}
              type="number"
              value={inputValues[unit]}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setInputValues((values) => ({ ...values, [unit]: value }));
              }}
              disabled={disabled}
              aria-label={unit}
              style={
                {
                  "--field-width": `${Math.max(inputValues[unit].length, 2)}ch`,
                } as React.CSSProperties
              }
            />
            <Text
              size="small"
              component="span"
              color={disabled ? colors.text.secondaryDisabled : colors.text.secondaryDefault}
            >
              {UNIT_LABELS[unit]}
            </Text>
          </Group>
        ))}
      </Group>
      {showIcon ? (
        <Box px={spacing[0.5]} style={{ display: "flex", alignItems: "center" }}>
          {onIconClick ? (
            <button
              type="button"
              aria-label={iconLabel}
              onClick={onIconClick}
              disabled={disabled}
              style={{
                display: "flex",
                alignItems: "center",
                margin: 0,
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: disabled ? "default" : "pointer",
                color: "inherit",
              }}
            >
              {trailingIcon}
            </button>
          ) : (
            trailingIcon
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export { InputDuration };
